import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { SidenavRoot } from "./SidenavRoot";
import { SidenavItem } from "./SidenavItem";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { canViewRoute, readUserRolesFromStorage } from "utils/routeAccess";
import { readUserGroupsFromStorage } from "utils/groupPolicyEngine";
import { useGroupAccess } from "context/GroupAccessContext";

function Sidenav({ color = "info", brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const [collapseName, setCollapseName] = useState("");
  const userRoles = readUserRolesFromStorage();

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const toggleSidenav = () => {
    setMiniSidenav(dispatch, !miniSidenav);
  };

  const closeSidenavOnMobile = () => {
    const isMobile = window.innerWidth < 1200;
    if (isMobile) setMiniSidenav(dispatch, true);
  };

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      const isMobile = window.innerWidth < 1200;

      // Keep desktop state user-controlled; only force mini mode on mobile.
      if (isMobile) {
        setMiniSidenav(dispatch, true);
      }

      setTransparentSidenav(dispatch, isMobile ? false : transparentSidenav);
      setWhiteSidenav(dispatch, isMobile ? false : whiteSidenav);
    }

    // The event listener that's calling the handleMiniSidenav function when resizing the window.
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location, transparentSidenav, whiteSidenav]);

  // Separate protected and non-protected routes
  const protectedRoutes = routes.filter((route) => route.protected);
  const nonProtectedRoutes = routes.filter((route) => !route.protected);

  const { restrictedSecApps, userAllowedSecApps, userDeniedSecApps } = useGroupAccess();

  // Render the routes
  const canViewRouteItem = (route) =>
    canViewRoute(route, {
      userGroups: readUserGroupsFromStorage(),
      userRoles,
      restrictedSecApps,
      userAllowedSecApps,
      userDeniedSecApps,
    });

  const renderRoutes = (routeList) => {
    return routeList
      .map((route) => {
        // Skip hidden routes
        if (route.hidden || !canViewRouteItem(route)) {
          return null;
        }

        if (route.type === "divider") {
          return (
            <Divider
              key={route.key}
              light={
                (!darkMode && !whiteSidenav && !transparentSidenav) ||
                (darkMode && !transparentSidenav && whiteSidenav)
              }
            />
          );
        }

        if (route.type === "title") {
          return miniSidenav ? null : (
            <MDTypography
              key={route.key}
              color={textColor}
              display="block"
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
              pl={3}
              mt={2}
              mb={1}
              ml={1}
            >
              {route.name}
            </MDTypography>
          );
        }

        // For collapse items, filter out hidden routes from the collapse array
        if (route.collapse) {
          route = {
            ...route,
            collapse: route.collapse.filter((item) => !item.hidden && canViewRouteItem(item)),
          };
          // If all subitems are hidden, skip this collapse item
          if (route.collapse.length === 0) {
            return null;
          }
        }

        return (
          <SidenavItem
            key={route.key}
            color={color}
            icon={route.icon}
            title={route.name}
            active={route.key === collapseName}
            noCollapse={route.noCollapse || miniSidenav}
            route={route.route}
            onClick={route.onClick || closeSidenavOnMobile}
            textColor={textColor}
            collapse={route.collapse}
            miniSidenav={miniSidenav}
          />
        );
      })
      .filter(Boolean); // Remove null entries
  };

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display="flex"
          alignItems="center"
          width="100%"
        >
          <MDBox
            onClick={toggleSidenav}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              width: "32px",
              height: "32px",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.1)",
              },
              marginRight: 2,
            }}
          >
            <Icon
              sx={{
                color: textColor === "dark" ? "text.primary" : "white.main",
              }}
            >
              {miniSidenav ? "menu" : "chevron_left"}
            </Icon>
          </MDBox>
          <MDBox
            display="flex"
            alignItems="center"
            flex={1}
          >
            {!miniSidenav && (
              <MDBox
                width={!brandName && "100%"}
                sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
              >
                <MDTypography
                  component="h6"
                  variant="button"
                  fontWeight="medium"
                  color={textColor}
                  sx={{
                    ml: 1,
                    transition: "opacity 0.3s ease",
                    opacity: miniSidenav ? 0 : 1,
                  }}
                >
                  {brandName}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List
        sx={{
          pt: 0,
          pb: 2,
        }}
      >
        {renderRoutes(protectedRoutes)}
      </List>
      <MDBox
        sx={{
          pt: 2,
          pb: 2,
          px: 2,
          mt: "auto",
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          opacity: 0.5,
        }}
      >
        <List>{renderRoutes(nonProtectedRoutes)}</List>
      </MDBox>
    </SidenavRoot>
  );
}

// Add PropTypes validation
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.node]),
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
      icon: PropTypes.node,
      route: PropTypes.string,
      protected: PropTypes.bool,
      noCollapse: PropTypes.bool,
      type: PropTypes.string,
      collapse: PropTypes.array,
      onClick: PropTypes.func,
    })
  ).isRequired,
};

// Default props
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

export default Sidenav;
